/**
 * Locale-aware date & number formatting bound to the user's selected UI
 * language (`uiLang` from AppContext).
 *
 * Why this exists: dates and numbers were being formatted with a hardcoded
 * locale (`'en-CH'`) or with no locale argument at all (browser default), so
 * they ignored the language toggle — a German user still saw English-shaped
 * dates. Route every user-facing date/number through here instead of calling
 * `toLocaleDateString` / `toLocaleString` directly.
 *
 * Usage in a component:
 *   const fmt = useFormat();
 *   fmt.date(session.end_date)         // → "12 Dez. 2024" in DE, "12 Dec 2024" in EN
 *   fmt.dateTime(msg.created_at)
 *   fmt.num(12345)                     // → "12'345" (de-CH) / "12,345" (en-GB)
 *
 * Outside a component (rare), call the pure functions with an explicit uiLang.
 */
import { useAppContext } from '../context/AppContext';
import type { UILang } from '../context/AppContext';

// EN → en-GB (DD/MM/YYYY, avoids US M/D/Y); DE/FR/IT → Swiss locales
// (apostrophe thousands separator, DD.MM.YYYY). Matches the convention that
// previously lived inline in Settings.tsx.
export const DATE_LOCALE: Record<UILang, string> = {
  EN: 'en-GB',
  DE: 'de-CH',
  FR: 'fr-CH',
  IT: 'it-CH',
};

const DEFAULT_DATE_OPTS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

const DEFAULT_DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
};

type DateInput = Date | string | number | null | undefined;

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(
  value: DateInput,
  uiLang: UILang,
  opts?: Intl.DateTimeFormatOptions,
  fallback = '—',
): string {
  const d = toDate(value);
  if (!d) return fallback;
  return d.toLocaleDateString(DATE_LOCALE[uiLang] ?? 'en-GB', opts ?? DEFAULT_DATE_OPTS);
}

/**
 * Locale-aware date with the STANDARD short month — "19. Juni 2026" (DE, Duden
 * standard: naturally short names like März/Mai/Juni/Juli stay full, others get
 * a trailing dot — Jan./Feb./Sept./Okt.), "19 Jun 2026" (EN), "19 giu 2026"
 * (IT), "19 juin 2026" (FR). THE list / overview convention. Use
 * {@link formatDateFull} for the full-name form on detail pages.
 *
 * NB: we intentionally use the official locale abbreviation (Intl `month:'short'`)
 * rather than forcing a uniform 3-letter form — the latter reads as English
 * ("Mär", "Jun") and isn't the Swiss/German standard.
 */
export function formatDateAbbrMonth(
  value: DateInput,
  uiLang: UILang,
  fallback = '—',
): string {
  return formatDate(value, uiLang, { day: 'numeric', month: 'short', year: 'numeric' }, fallback);
}

/**
 * Standard short month WITHOUT the year — "1. Juni" (DE), "1 Jun" (EN). For
 * compact list contexts like a same-year date range (`1. Juni – 19. Juni 2026`).
 */
export function formatDayMonthAbbr(
  value: DateInput,
  uiLang: UILang,
  fallback = '—',
): string {
  return formatDate(value, uiLang, { day: 'numeric', month: 'short' }, fallback);
}

/**
 * Locale-aware date with the FULL month name — "19. Juni 2026" (DE),
 * "19 June 2026" (EN). THE detail-page convention (counterpart to
 * {@link formatDateAbbrMonth}).
 */
export function formatDateFull(
  value: DateInput,
  uiLang: UILang,
  fallback = '—',
): string {
  return formatDate(value, uiLang, { day: 'numeric', month: 'long', year: 'numeric' }, fallback);
}

export function formatDateTime(
  value: DateInput,
  uiLang: UILang,
  opts?: Intl.DateTimeFormatOptions,
  fallback = '—',
): string {
  const d = toDate(value);
  if (!d) return fallback;
  return d.toLocaleString(DATE_LOCALE[uiLang] ?? 'en-GB', opts ?? DEFAULT_DATETIME_OPTS);
}

export function formatNumber(
  value: number | null | undefined,
  uiLang: UILang,
  opts?: Intl.NumberFormatOptions,
  fallback = '—',
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback;
  return value.toLocaleString(DATE_LOCALE[uiLang] ?? 'en-GB', opts);
}

/**
 * Hook returning formatters bound to the current UI language. Prefer this in
 * components over the pure functions so call sites stay terse.
 */
export function useFormat() {
  const { uiLang } = useAppContext();
  return {
    date: (v: DateInput, opts?: Intl.DateTimeFormatOptions, fallback?: string) =>
      formatDate(v, uiLang, opts, fallback),
    dateAbbr: (v: DateInput, fallback?: string) =>
      formatDateAbbrMonth(v, uiLang, fallback),
    dayMonthAbbr: (v: DateInput, fallback?: string) =>
      formatDayMonthAbbr(v, uiLang, fallback),
    dateFull: (v: DateInput, fallback?: string) =>
      formatDateFull(v, uiLang, fallback),
    dateTime: (v: DateInput, opts?: Intl.DateTimeFormatOptions, fallback?: string) =>
      formatDateTime(v, uiLang, opts, fallback),
    num: (v: number | null | undefined, opts?: Intl.NumberFormatOptions, fallback?: string) =>
      formatNumber(v, uiLang, opts, fallback),
  };
}
